import ActionDialog from "@dashboard/components/ActionDialog";
import {
  WsmErrorFragment,
  WsmFitmentAxisFragment,
  WsmFitmentQualifierFragment,
} from "@dashboard/graphql";
import { Box, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { getWsmFitmentErrorMessage, getWsmFitmentFieldError } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import { applicationMessages, sectionMessages } from "../messages";
import { WsmFitmentValuePick } from "../types";
import { getFitmentLabel } from "../utils";
import { WsmFitmentAxisValuePicker, WsmFitmentListPicker } from "./WsmFitmentValuePicker";

/** What the dialog hands back: one pick per axis id and per qualifier id, blanks left out. */
export interface WsmFitmentApplicationDraft {
  axisPicks: Record<string, WsmFitmentValuePick | undefined>;
  qualifierPicks: Record<string, WsmFitmentValuePick | undefined>;
}

export interface WsmFitmentApplicationAddDialogProps {
  open: boolean;
  /** The schema's axes in ask order. */
  axes: WsmFitmentAxisFragment[];
  /** Every qualifier the store has, each with its values. */
  qualifiers: WsmFitmentQualifierFragment[];
  errors: WsmErrorFragment[];
  disabled: boolean;
  onClose: () => void;
  /** Resolves to true when the application was added and the dialog can close. */
  onConfirm: (draft: WsmFitmentApplicationDraft) => Promise<boolean>;
}

/**
 * One application: a value per axis (or none, meaning any) plus optional
 * qualifier values. A typed value the axis does not have yet is created on the
 * way through, by the idempotent bulk create; the view does that resolution.
 */
export const WsmFitmentApplicationAddDialog = ({
  open,
  axes,
  qualifiers,
  errors,
  disabled,
  onClose,
  onConfirm,
}: WsmFitmentApplicationAddDialogProps) => {
  const intl = useIntl();
  const [axisPicks, setAxisPicks] = useState<WsmFitmentApplicationDraft["axisPicks"]>({});
  const [qualifierPicks, setQualifierPicks] = useState<
    WsmFitmentApplicationDraft["qualifierPicks"]
  >({});
  const attempt = useWsmFitmentAttempt();

  // Every open starts blank and neutral: picks from a cancelled or failed try,
  // and that try's button state, do not carry over. (The page clears errors.)
  useEffect(() => {
    if (open) {
      setAxisPicks({});
      setQualifierPicks({});
      attempt.reset();
    }
    // Only the open edge matters; reset is a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nothingPicked = !Object.values(axisPicks).some(pick => pick?.text.trim());
  // The bulk create names a refused row items.0.values or items.0.qualifierValues;
  // the view files a value resolution error under the same two, by the list it
  // was resolving. Anything else is general.
  const valuesError = getWsmFitmentFieldError(errors, "items.0.values");
  const qualifiersError = getWsmFitmentFieldError(errors, "items.0.qualifierValues");
  const generalErrors = errors.filter(error => error !== valuesError && error !== qualifiersError);

  return (
    <ActionDialog
      open={open}
      size="sm"
      title={intl.formatMessage(applicationMessages.add)}
      confirmButtonState={attempt.state}
      disabled={disabled || nothingPicked}
      onClose={onClose}
      onConfirm={() =>
        attempt.run(async () => ({ saved: await onConfirm({ axisPicks, qualifierPicks }) }))
      }
    >
      <Text size={2} color="default2" display="block" marginBottom={4}>
        {intl.formatMessage(applicationMessages.addHelp)}
      </Text>
      <Box display="grid" gap={4}>
        {axes.map(axis => (
          <WsmFitmentAxisValuePicker
            key={axis.id}
            axisId={axis.id}
            label={getFitmentLabel(axis)}
            disabled={disabled}
            value={axisPicks[axis.id]}
            onChange={pick => setAxisPicks(current => ({ ...current, [axis.id]: pick }))}
            data-test-id={`wsm-fitment-application-axis-${axis.id}`}
          />
        ))}
        {nothingPicked && (
          <Text size={2} color="default2">
            {intl.formatMessage(applicationMessages.addNothing)}
          </Text>
        )}
        {valuesError && (
          <Text size={2} color="critical1" data-test-id="wsm-fitment-application-values-error">
            {getWsmFitmentErrorMessage(valuesError, intl)}
          </Text>
        )}
      </Box>
      {qualifiers.length > 0 && (
        <Box display="grid" gap={4} marginTop={6}>
          <Box>
            <Text size={3} fontWeight="bold" display="block">
              {intl.formatMessage(sectionMessages.qualifiers)}
            </Text>
            <Text size={2} color="default2" display="block">
              {intl.formatMessage(applicationMessages.addQualifiersHelp)}
            </Text>
          </Box>
          {/* One single-select per qualifier, its pick keyed by the qualifier's id:
              picking again replaces the value, so an application never carries
              two values of one qualifier (DUPLICATE_QUALIFIER_IN_APPLICATION). */}
          {qualifiers.map(qualifier => (
            <WsmFitmentListPicker
              key={qualifier.id}
              label={getFitmentLabel(qualifier)}
              values={qualifier.values}
              disabled={disabled}
              value={qualifierPicks[qualifier.id]}
              onChange={pick =>
                setQualifierPicks(current => ({ ...current, [qualifier.id]: pick }))
              }
              data-test-id={`wsm-fitment-application-qualifier-${qualifier.id}`}
            />
          ))}
          {qualifiersError && (
            <Text
              size={2}
              color="critical1"
              data-test-id="wsm-fitment-application-qualifiers-error"
            >
              {getWsmFitmentErrorMessage(qualifiersError, intl)}
            </Text>
          )}
        </Box>
      )}
      {generalErrors.map((error, index) => (
        <Text
          key={index}
          size={2}
          color="critical1"
          display="block"
          marginTop={4}
          data-test-id="wsm-fitment-application-error"
        >
          {getWsmFitmentErrorMessage(error, intl)}
        </Text>
      ))}
    </ActionDialog>
  );
};

export default WsmFitmentApplicationAddDialog;
